import { Avatar, Box, MenuItem, Select, SelectChangeEvent, type SxProps, type Theme } from '@mui/material';
import { t } from 'i18next';
import { useMemo } from 'react';
import SimpleUser from '../../features/auth/types/SimpleUser';
import User from '../../features/auth/types/User';
import UserAvatar from '../custom/avatar/UserAvatar';
import {
	filterSelectAllAvatarSx,
	filterSelectAllLabelSx,
	filterSelectPlayerMenuItemSx,
	filterSelectPlayerMenuProps,
	filterSelectPlayerLayoutSx,
	filterSelectRootSx,
} from './filterSelectStyles';
import { sortPlayersForSelect } from './sortPlayersForSelect';

interface PlayerSelectProps {
	value: string;
	onChange: ((event: SelectChangeEvent<string>) => void) | undefined;
	players: Array<User | SimpleUser> | undefined;
	sx?: SxProps<Theme>;
}

const PlayerSelect = ({ value, onChange, players, sx }: PlayerSelectProps): JSX.Element => {
	const sortedPlayers = useMemo(
		() => (players ? sortPlayersForSelect(players) : []),
		[players]
	);
	const menuItemCount = 1 + sortedPlayers.length;
	const extraSx = sx == null ? [] : Array.isArray(sx) ? sx : [sx];
	const allValue = t('all');
	const safeValue =
		value === allValue || sortedPlayers.some((player) => player.username === value)
			? value
			: allValue;

	return (
		<Select
			size="small"
			sx={
				[
					filterSelectRootSx('standard'),
					filterSelectPlayerLayoutSx,
					...(sx == null ? [{ minWidth: { xs: 0, sm: '10rem' } }] : extraSx),
				] as SxProps<Theme>
			}
			labelId="player-title-label"
			id="player-title-select"
			value={safeValue}
			onChange={onChange}
			MenuProps={filterSelectPlayerMenuProps(menuItemCount)}
		>
			<MenuItem sx={filterSelectPlayerMenuItemSx} value={t('all')}>
				<Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
					<Avatar
						variant="circular"
						sx={filterSelectAllAvatarSx}
						alt="all_players_logo"
						src={`${import.meta.env.PUBLIC_URL || ''}/upload/avatars/cool_man.jpg`}
					/>
					<Box component="span" sx={[filterSelectAllLabelSx, { ml: 0.5, mr: 0 }] as SxProps<Theme>}>
						{t('all')}
					</Box>
				</Box>
			</MenuItem>
			{sortedPlayers.map((p) => (
						<MenuItem
							key={p.id}
							sx={filterSelectPlayerMenuItemSx}
							value={p.username}
						>
							<UserAvatar
								player={p}
								height={28}
								sx={{ my: 0, mb: 0, ml: 0, fontWeight: 500, fontSize: '0.875rem' }}
								avasx={{ border: 0, mr: 0.75, height: 28, width: 28 }}
							/>
						</MenuItem>
					))}
		</Select>
	);
};

export default PlayerSelect;
